import { useJobCommissionBreakdown } from '../hooks/useJobCommissionBreakdown';
import { Loader2 } from 'lucide-react';

interface JobCommissionBreakdownProps {
  jobId: number;
}

export function JobCommissionBreakdown({ jobId }: JobCommissionBreakdownProps) {
  const { data, isLoading, error } = useJobCommissionBreakdown(jobId);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatPercent = (percent: number) => {
    return `${(percent * 100).toFixed(0)}%`;
  };

  if (isLoading) {
    return (
      <div className="bg-card p-1.5 rounded-[20px] border border-border">
        <h3 className="text-secondary font-semibold text-sm mb-1">Commission Breakdown</h3>
        <div className="flex items-center justify-center py-4">
          <Loader2 className="h-5 w-5 animate-spin text-secondary" />
        </div>
      </div>
    );
  }

  if (error || !data) {
    return null;
  }

  if (data.commissions.length === 0) {
    return (
      <div className="bg-card p-1.5 rounded-[20px] border border-border">
        <h3 className="text-secondary font-semibold text-sm mb-1">Commission Breakdown</h3>
        <p className="text-xs text-muted-foreground">No commissions configured for this job.</p>
      </div>
    );
  }

  return (
    <div className="bg-card p-1.5 rounded-[20px] border border-border">
      <h3 className="text-secondary font-semibold text-sm mb-2">Commission Breakdown</h3>

      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-1 px-1 font-semibold text-muted-foreground">Name</th>
              <th className="text-left py-1 px-1 font-semibold text-muted-foreground">Type</th>
              <th className="text-right py-1 px-1 font-semibold text-muted-foreground">Rate</th>
              <th className="text-right py-1 px-1 font-semibold text-muted-foreground">Amount</th>
              <th className="text-right py-1 px-1 font-semibold text-muted-foreground">Paid</th>
              <th className="text-right py-1 px-1 font-semibold text-muted-foreground">Due</th>
            </tr>
          </thead>
          <tbody>
            {data.commissions.map((commission, index) => (
              <tr key={index} className="border-b border-[#f0f0f0]">
                <td className="py-1 px-1 font-medium">{commission.personName}</td>
                <td className="py-1 px-1 text-muted-foreground">{commission.commissionType}</td>
                <td className="py-1 px-1 text-right">{formatPercent(commission.commissionRate)}</td>
                <td className={`py-1 px-1 text-right ${commission.commissionAmount < 0 ? 'text-red-600' : ''}`}>
                  {formatCurrency(commission.commissionAmount)}
                </td>
                <td className="py-1 px-1 text-right">{formatCurrency(commission.paidToDate)}</td>
                <td className={`py-1 px-1 text-right font-semibold ${commission.amountDue < 0 ? 'text-red-600' : ''}`}>
                  {formatCurrency(commission.amountDue)}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-muted">
              <td colSpan={3} className="py-1 px-1 font-semibold">Total Commission</td>
              <td className={`py-1 px-1 text-right font-semibold ${data.totalCommission < 0 ? 'text-red-600' : ''}`}>
                {formatCurrency(data.totalCommission)}
              </td>
              <td colSpan={2}></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
